import { useMemo, useState } from 'react';
import {
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  ClaimTableRow,
  isClaimTariffRow,
} from 'pages/claims/utils/claimTableRows';
import { buttonTextColor } from 'utils/buttonUtils';
import {
  hasPaidTariffDebits,
  PaidTariffPaymentSummary,
} from './PaidTariffPaymentSummary';

interface ResubmitPreviewRow {
  claimID: string;
  claimLabel: string;
  tariffLabel: string;
  currentStatus: string;
}

interface ClaimBulkResubmitModalProps {
  open: boolean;
  rows: ClaimTableRow[];
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  action?: 'ready' | 'resubmit';
}

const getTariffLabel = (row: ClaimTableRow) => {
  const tariff = row.tariffs?.[0];
  if (!tariff) return 'Tariff';
  return `${tariff.prefix ?? ''}${tariff.code ?? ''}`.trim() || 'Tariff';
};

const buildPreviewRow = (row: ClaimTableRow): ResubmitPreviewRow => {
  const tariff = row.tariffs?.[0];
  const claimLabel =
    row.claimNumber?.trim() ||
    row.parentClaim?.claimNumber?.trim() ||
    row.parentClaimId ||
    row.id;

  return {
    claimID: row.parentClaimId || row.id,
    claimLabel,
    tariffLabel: getTariffLabel(row),
    currentStatus: tariff?.status?.trim() || 'Unknown',
  };
};

export const ClaimBulkResubmitModal = ({
  open,
  rows,
  onClose,
  onConfirm,
  action = 'resubmit',
}: ClaimBulkResubmitModalProps) => {
  const theme = useMantineTheme();
  const mode = useComputedColorScheme('dark');
  const [loading, setLoading] = useState(false);

  const previewRows = useMemo(
    () =>
      rows
        .flatMap((row) => (isClaimTariffRow(row) ? [row] : row.subRows ?? []))
        .map(buildPreviewRow),
    [rows]
  );
  const paymentSummary = useMemo(() => {
    const tariffRows = rows.flatMap((row) =>
      isClaimTariffRow(row) ? [row] : row.subRows ?? []
    );

    return {
      tariffs: tariffRows.flatMap((row) => row.tariffs ?? []),
      debits: tariffRows.flatMap((row) =>
        row.debits?.length
          ? row.debits
          : (row.parentClaim?.debits ?? []).filter(
              (debit) => debit.tariffID === row.tariffId
            )
      ),
      timezone: tariffRows[0]?.parentClaim?.site?.timezone,
    };
  }, [rows]);
  const hasPayments = hasPaidTariffDebits(paymentSummary.debits);

  const manyRows = previewRows.length > 5;
  const rowLabel = previewRows.length === 1 ? 'row' : 'rows';
  const title =
    action === 'ready'
      ? 'Mark Tariffs Ready'
      : previewRows.length === 1
      ? 'Resubmit Selected Row'
      : 'Resubmit Selected Rows';

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await Promise.resolve(onConfirm());
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      closeOnEscape
      withCloseButton
      size="md"
      centered
      radius="lg"
      shadow="xl"
      overlayProps={{ blur: 3, opacity: 0.5 }}
      title={<Text fw={700}>{title}</Text>}
      styles={{
        content: {
          background:
            mode === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border:
            mode === 'dark'
              ? `1px solid ${theme.colors.dark[4]}`
              : `1px solid ${theme.colors.gray[3]}`,
        },
        header: { paddingBottom: 0 },
        title: {
          color: mode === 'dark' ? theme.white : undefined,
        },
        close: {
          border: `1px solid ${
            mode === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]
          }`,
          background: mode === 'dark' ? theme.colors.dark[6] : theme.white,
          borderRadius: 10,
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${
              mode === 'dark' ? theme.colors.orange[6] : theme.colors.orange[5]
            }`,
          },
        },
      }}
    >
      <Stack gap="md">
        {hasPayments ? (
          <PaidTariffPaymentSummary {...paymentSummary} showWarning />
        ) : (
          <Text size="sm">
            Review the selected {rowLabel} before{' '}
            {action === 'ready' ? 'marking ready' : 'resubmitting'}. This will
            move the selected tariff(s) to Ready.
          </Text>
        )}

        {manyRows ? (
          <Paper withBorder radius="md" p="md">
            <Stack gap="xs">
              {previewRows.slice(0, 3).map((row) => (
                <Stack key={`${row.claimID}-${row.tariffLabel}`} gap={2}>
                  <Text fw={600}>
                    {row.claimLabel} · {row.tariffLabel}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Current status{' '}
                    <Text component="span" c="yellow.7" fw={600}>
                      {row.currentStatus}
                    </Text>{' '}
                    · Will become{' '}
                    <Text component="span" c="green" fw={600}>
                      Ready
                    </Text>
                  </Text>
                </Stack>
              ))}
              {previewRows.length > 3 ? (
                <Text size="sm" c="dimmed">
                  +{previewRows.length - 3} more selected rows
                </Text>
              ) : null}
            </Stack>
          </Paper>
        ) : (
          <Stack gap="sm">
            {previewRows.map((row) => (
              <Paper
                key={`${row.claimID}-${row.tariffLabel}`}
                withBorder
                radius="md"
                p="md"
              >
                <Stack gap={4}>
                  <Text fw={600}>
                    {row.claimLabel} · {row.tariffLabel}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Current status{' '}
                    <Text component="span" c="yellow.7" fw={600}>
                      {row.currentStatus}
                    </Text>{' '}
                    · Will become{' '}
                    <Text component="span" c="green" fw={600}>
                      Ready
                    </Text>
                  </Text>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        <Group grow align="stretch">
          <Button
            color="gray"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            c={buttonTextColor(mode, loading)}
          >
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleConfirm}
            loading={loading}
            disabled={loading}
            c={buttonTextColor(mode, loading)}
          >
            {action === 'ready'
              ? `Mark tariff${previewRows.length === 1 ? '' : 's'} ready`
              : `Resubmit tariff${previewRows.length === 1 ? '' : 's'}`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
